import React, { useEffect, useRef, useState } from 'react';
import { AlarmClock } from 'lucide-react';

// Themed GlassModal — uses the active theme's Aurora tokens (var(--au-*)).
export default function IdleWarningModal({ open, warningSeconds = 60, onStay, onLogout }) {
  const [secondsLeft, setSecondsLeft] = useState(warningSeconds);
  const stayRef = useRef(null);

  useEffect(() => {
    if (!open) { setSecondsLeft(warningSeconds); return; }
    const interval = setInterval(() => {
      setSecondsLeft(s => {
        if (s <= 1) { clearInterval(interval); onLogout(); return 0; }
        return s - 1;
      });
    }, 1000);
    return () => clearInterval(interval);
  }, [open, warningSeconds, onLogout]);

  useEffect(() => {
    if (!open) return;
    stayRef.current?.focus();
    const onKey = (e) => { if (e.key === 'Escape') onStay(); };
    document.addEventListener('keydown', onKey);
    return () => document.removeEventListener('keydown', onKey);
  }, [open, onStay]);

  if (!open) return null;

  const minutes = Math.floor(secondsLeft / 60);
  const seconds = secondsLeft % 60;
  const timeStr = `${minutes}:${seconds.toString().padStart(2, '0')}`;
  const progress = (secondsLeft / warningSeconds) * 100;

  return (
    <div
      role="dialog" aria-modal="true" aria-labelledby="idle-title"
      style={{
        position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.55)',
        backdropFilter: 'blur(8px)', WebkitBackdropFilter: 'blur(8px)',
        display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 99999, padding: 20,
      }}
      onMouseDown={(e) => { if (e.target === e.currentTarget) onStay(); }}
    >
      <div style={{
        background: 'var(--au-glass-strong)', color: 'var(--au-text)',
        WebkitBackdropFilter: 'var(--au-blur-strong)', backdropFilter: 'var(--au-blur-strong)',
        border: '1px solid var(--au-border)', borderRadius: 'var(--au-r-lg)',
        boxShadow: 'var(--au-shadow-float)', padding: '34px 30px', maxWidth: 440, width: '100%', textAlign: 'center',
        fontFamily: 'var(--au-font)',
      }}>
        <span style={{
          width: 56, height: 56, borderRadius: 16, display: 'inline-grid', placeItems: 'center',
          background: 'var(--au-accent)', color: 'var(--au-btn-text)', boxShadow: 'var(--au-glow)', marginBottom: 14,
        }}>
          <AlarmClock size={28} strokeWidth={2} aria-hidden="true" />
        </span>
        <h2 id="idle-title" style={{ fontFamily: 'var(--au-font-display)', fontSize: 22, fontWeight: 700, margin: '0 0 8px', color: 'var(--au-text)' }}>
          Are you still there?
        </h2>
        <p style={{ fontSize: 14, color: 'var(--au-text-2)', margin: '0 0 22px', lineHeight: 1.55 }}>
          For your security, we'll automatically log you out due to inactivity.
        </p>
        <div style={{
          background: 'var(--au-glass)', borderRadius: 14, padding: '16px 20px', marginBottom: 22,
          border: '1px solid var(--au-border)', position: 'relative', overflow: 'hidden',
        }}>
          <div style={{ position: 'absolute', bottom: 0, left: 0, height: 3, width: `${progress}%`, background: 'var(--au-accent)', transition: 'width 1s linear' }} />
          <div style={{ fontSize: 11, color: 'var(--au-muted)', textTransform: 'uppercase', letterSpacing: 1, marginBottom: 4 }}>Auto-logout in</div>
          <div style={{ fontSize: 38, fontWeight: 700, color: 'var(--au-accent-ink)', fontFamily: 'var(--au-font-mono)' }}>{timeStr}</div>
        </div>
        <div style={{ display: 'flex', gap: 12 }}>
          <button onClick={onLogout} style={{
            flex: 1, padding: '12px 16px', border: '1px solid var(--au-border)', background: 'var(--au-glass)',
            color: 'var(--au-text-2)', borderRadius: 'var(--au-r-pill)', fontSize: 14, fontWeight: 600, cursor: 'pointer', fontFamily: 'var(--au-font)',
          }}>Logout now</button>
          <button ref={stayRef} onClick={onStay} style={{
            flex: 1, padding: '12px 16px', border: 'none', background: 'var(--au-accent)', color: 'var(--au-btn-text)',
            borderRadius: 'var(--au-r-pill)', fontSize: 14, fontWeight: 700, cursor: 'pointer', boxShadow: 'var(--au-glow)', fontFamily: 'var(--au-font)',
          }}>Stay logged in</button>
        </div>
      </div>
    </div>
  );
}
